import { API_BASE_URL } from '../config';
import { Quote, QuoteUpdatePayload } from '../types';
import { apiRequest } from './api-client';

export interface DateRangeFilter {
  from: Date | undefined;
  to: Date | undefined;
}

export interface FetchQuotesOptions {
  status?: 'all' | 'pending' | 'approved' | 'rejected';
  dateRange?: DateRangeFilter;
}

export async function fetchQuotes(accessToken: string, options?: FetchQuotesOptions): Promise<Quote[]> {
  const params = new URLSearchParams();

  // Add status filter if not 'all'
  if (options?.status && options.status !== 'all') {
    params.append('status', options.status);
  }

  // Add date range filter
  if (options?.dateRange?.from) {
    const startDate = new Date(options.dateRange.from);
    startDate.setHours(0, 0, 0, 0);
    params.append('startDate', startDate.toISOString().slice(0, -1)); // Remove Z for LocalDateTime

    // If 'to' is set, use it; otherwise use same day as 'from'
    const endDate = options.dateRange.to ? new Date(options.dateRange.to) : new Date(options.dateRange.from);
    endDate.setHours(23, 59, 59, 999);
    params.append('endDate', endDate.toISOString().slice(0, -1));
  }

  // Load all quotes (default is 20, increase to get all)
  params.append('size', '1000');

  const queryString = params.toString();
  const path = `/backoffice/quotes${queryString ? '?' + queryString : ''}`;

  const result = await apiRequest<Quote[]>(path, accessToken);
  return result || [];
}

export async function markQuoteAsViewed(quoteId: string, accessToken: string): Promise<void> {
  await apiRequest(`/backoffice/quotes/${quoteId}/viewed`, accessToken, { method: 'POST' });
}

export async function exportQuotesPDF(accessToken: string): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}/backoffice/quotes/export/pdf`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error('Erro ao exportar orçamentos');
  }

  return await response.blob();
}

export async function downloadQuotePDF(quoteId: string, accessToken: string): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}/quotes/${quoteId}/pdf`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error('Erro ao baixar PDF do orçamento');
  }

  return await response.blob();
}

export async function updateQuoteStatus(
  quoteId: string,
  status: 'pending' | 'approved' | 'rejected',
  accessToken: string
): Promise<void> {
  await apiRequest(`/backoffice/quotes/${quoteId}/status`, accessToken, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export interface CreateQuotePayload {
  customer: {
    name: string;
    company?: string;
    document: string;
    email: string;
    phone: string;
  };
  items?: Array<{
    materialId: string;
    width: number;
    height: number;
    thickness: number;
    quantity: number;
    color?: string;
  }>;
  customItems?: Array<{
    description: string;
    value: number;
  }>;
  notes?: string;
}

export async function createQuote(
  payload: CreateQuotePayload,
  accessToken: string
): Promise<Quote> {
  return apiRequest<Quote>('/quotes', accessToken, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateQuote(
  quoteId: string,
  payload: QuoteUpdatePayload,
  accessToken: string
): Promise<Quote> {
  return apiRequest<Quote>(`/backoffice/quotes/${quoteId}`, accessToken, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}
